import { FormattedMessage } from "react-intl";
import { useEcgDetailsContext } from "../../../context/EcgDetailsContext";
import Card from "../../Shared/Card/Card";
import DateTime from "../../Shared/DateTime/DateTime";
import Comments from "./Comments/Comments";
import EcgDraft from "./EcgDraft/EcgDraft";
import styles from "./EcgMeta.module.css";
import EcgMetaHeader from "./EcgMetaHeader/EcgMetaHeader";
import Electrodes from "./Electrodes/Electrodes";
import HasPacemaker from "./HasPacemaker/HasPacemaker";
import Measurements from "./Measurements/Measurements";
import MetaCard from "./MetaCard/MetaCard";
import MetaFindings from "./MetaFindings/MetaFindings";
import MetaInterpretations from "./MetaInterpretations/MetaInterpretations";
import MetaNotes from "./MetaNotes/MetaNotes";
import ReviewedBanner from "./ReviewdBanner/ReviewedBanner";
import Scale from "./Scale/Scale";
import Symptoms from "./Symptoms/Symptoms";

const EcgMeta = () => {
  const {
    state: { selectedEcg, compareEcg, draft, loading },
  } = useEcgDetailsContext();

  return (
    <div className="h-100 d-flex flex-column">
      <div className={`${styles.header} p-2 overflow-auto`}>
        {!selectedEcg || loading ? (
          <span className="font-md muted-color">
            <strong>
              <FormattedMessage
                id="USER_DETAILS.EMPTY_TITLE"
                defaultMessage="Patient details will show here"
              />
            </strong>
          </span>
        ) : (
          <EcgMetaHeader
            reference={selectedEcg?.reference || ""}
            deviceSerialNumber={selectedEcg?.deviceSerialNumber || ""}
            phone={selectedEcg?.patient?.phone || ""}
            email={selectedEcg?.patient?.email || ""}
          />
        )}
      </div>
      <div className={`${styles.meta} p-2 flex-grow-1 overflow-auto`}>
        {!selectedEcg || loading ? (
          <span className="font-md muted-color">
            <strong>
              <FormattedMessage
                id="ECG_DETAILS.EMPTY_TITLE"
                defaultMessage="ECG details will show here"
              />
            </strong>
          </span>
        ) : (
          <>
            {compareEcg && (
              <>
                <MetaCard compare className="mb-2">
                  <div className="font-md">
                    <p className="mb-2 prim-color-dark font-weight-bold">
                      <FormattedMessage
                        id="ECG_DETAILS.COMPARED_WITH"
                        defaultMessage="Compared with"
                      />
                    </p>
                    <p className="mb-1">
                      <strong>
                        <DateTime showTime>{compareEcg.date}</DateTime>
                      </strong>
                    </p>
                    <EcgMetaHeader
                      reference={compareEcg.reference || ""}
                      deviceSerialNumber={compareEcg.deviceSerialNumber || ""}
                      hideContactInfo
                    />
                  </div>
                </MetaCard>
              </>
            )}
            <HasPacemaker hasPacemaker={selectedEcg.hasPacemaker} />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <HasPacemaker compare hasPacemaker={selectedEcg.hasPacemaker} />
                <div className="mb-2"></div>
              </>
            )}
            <Measurements measurements={selectedEcg?.measurements} />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <Measurements compare measurements={compareEcg?.measurements} />
                <div className="mb-2"></div>
              </>
            )}
            <MetaFindings
              findings={selectedEcg.findings}
              showSuggestive={selectedEcg.showSuggestive}
              ecgReviewState={selectedEcg.ecgReviewState}
            />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <MetaFindings findings={compareEcg?.findings} compare />
                <div className="mb-2"></div>
              </>
            )}
            <Symptoms symptoms={selectedEcg?.symptoms} />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <Symptoms compare symptoms={compareEcg?.symptoms} />
                <div className="mb-2"></div>
              </>
            )}
            <Comments comments={selectedEcg?.comments} />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <Comments compare comments={compareEcg?.comments} />
                <div className="mb-2"></div>
              </>
            )}

            <MetaInterpretations
              interpretation={selectedEcg?.interpretation}
              actions={selectedEcg?.actions}
              ecgReviewState={selectedEcg?.ecgReviewState}
            />
            <div className="mb-2"></div>
            {compareEcg && (
              <>
                <MetaInterpretations
                  compare
                  interpretation={compareEcg?.interpretation}
                  actions={compareEcg?.actions}
                />
                <div className="mb-2"></div>
              </>
            )}

            <MetaNotes notes={selectedEcg?.notes ?? []} />
            <div className="mb-2"></div>
            {selectedEcg?.isApproved && (
              <ReviewedBanner reviewerInfo={selectedEcg?.approval} />
            )}

            {draft.show && <EcgDraft />}
            <div className="mb-2"></div>
            <Electrodes electrodes={selectedEcg?.electrodesStatus ?? {}} />
            <div className="mb-2"></div>

            <Scale />
            {selectedEcg?.isUrgent && <UrgentBanner />}
            {selectedEcg?.isReadable === false && <UnreadableBanner />}
          </>
        )}
      </div>
    </div>
  );
};

export default EcgMeta;

const UrgentBanner = () => (
  <Card className="text-center prim-bg-color-dark font-md">
    <strong>
      <FormattedMessage
        id="ECG_DETAILS.FLAGGED_URGENT"
        defaultMessage="Flagged as High Priority"
      />
    </strong>
  </Card>
);

const UnreadableBanner = () => (
  <Card className="text-center prim-bg-color-dark font-md">
    <strong>
      <FormattedMessage
        id="ECG_DETAILS.MARKED_AS_UNREADABLE"
        defaultMessage="Marked as Unreadable"
      />
    </strong>
  </Card>
);
